"use client";

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

export default function CustomOrders() {
  const [formData, setFormData] = useState({
    productType: '',
    quantity: '',
    purity: '',
    message: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      const response = await fetch('/api/send-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          formType: 'custom-order',
          formData: formData,
        }),
      });

      const result = await response.json();

      if (response.ok) {
        setSubmitMessage('Thank you for your inquiry! We will respond within 24 hours.');
        setFormData({ productType: '', quantity: '', purity: '', message: '' });
      } else {
        setSubmitMessage('Failed to send inquiry. Please try again or contact us directly at fastminerals9@gmail.com');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitMessage('Failed to send inquiry. Please try again or contact us directly at fastminerals9@gmail.com');
    } finally {
      setIsSubmitting(false);
      // Clear message after 5 seconds
      setTimeout(() => setSubmitMessage(''), 5000);
    }
  };

  return (
    <section className="py-32 bg-card">
      <div className="container mx-auto px-6">
        <div className="max-w-4xl mx-auto bg-background rounded-[40px] p-12 shadow-2xl">
          <div className="text-center mb-12">
            <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center mx-auto mb-6">
              <Icon name="WrenchScrewdriverIcon" size={32} className="text-primary-foreground" />
            </div>
            <h2 className="text-4xl md:text-5xl font-serif italic font-bold tracking-tight mb-4 text-foreground">
              Custom Orders
            </h2>
            <p className="text-lg text-muted max-w-2xl mx-auto">
              Need specific quantities, grades, or special requirements? We can help.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="productType" className="block text-sm font-bold text-foreground mb-2">
                  Product Type *
                </label>
                <select
                  id="productType"
                  required
                  value={formData.productType}
                  onChange={(e) => setFormData({ ...formData, productType: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl border border-border bg-input text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                >
                  <option value="">Select product type</option>
                  <option value="gold">Gold</option>
                  <option value="precious">Precious Minerals</option>
                  <option value="semi-precious">Semi-Precious Minerals</option>
                  <option value="custom">Custom Request</option>
                </select>
              </div>

              <div>
                <label htmlFor="quantity" className="block text-sm font-bold text-foreground mb-2">
                  Quantity Needed *
                </label>
                <input
                  type="text"
                  id="quantity"
                  required
                  placeholder="e.g., 500g, 100 carats"
                  value={formData.quantity}
                  onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl border border-border bg-input text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                />
              </div>
            </div>

            <div>
              <label htmlFor="purity" className="block text-sm font-bold text-foreground mb-2">
                Purity/Grade Requirements
              </label>
              <input
                type="text"
                id="purity"
                placeholder="e.g., 99.9% pure, AAA grade"
                value={formData.purity}
                onChange={(e) => setFormData({ ...formData, purity: e.target.value })}
                className="w-full px-4 py-3 rounded-xl border border-border bg-input text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>

            <div>
              <label htmlFor="message" className="block text-sm font-bold text-foreground mb-2">
                Additional Requirements *
              </label>
              <textarea
                id="message"
                required
                rows={6}
                placeholder="Describe your specific requirements, delivery timeline, or any special requests..."
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                className="w-full px-4 py-3 rounded-xl border border-border bg-input text-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-none"
              />
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <span className="flex items-center justify-center gap-2">
                  <Icon name="ArrowPathIcon" size={20} className="animate-spin" />
                  Sending...
                </span>
              ) : (
                'Submit Custom Order Inquiry'
              )}
            </button>

            {/* Success/Error Message */}
            {submitMessage && (
              <div
                className={`rounded-xl p-4 text-center ${
                  submitMessage.includes('Failed')
                    ? 'bg-red-50 border border-red-200 text-red-700' :'bg-success/10 border border-success text-success'
                }`}
              >
                <p className="font-medium">{submitMessage}</p>
              </div>
            )}
          </form>
        </div>
      </div>
    </section>
  );
}